/**
 * AccountSettingsPage — /account/settings
 *
 * Apple 5.1.1(v)-compliant in-app account management UI.
 *  • Per-module deletion (voice-callable: "delete my Tackle account")
 *  • Per-list email preferences (voice-callable: "unsubscribe from Plaza promos")
 *  • Biometric (passkey) enrolment for Face ID / Touch ID / fingerprint
 *  • Sign in with Apple (linked-account display)
 *
 * Every actionable button registers itself with VoiceActionsContext,
 * so saying "delete my fish-map account" fires the same flow as a
 * tap. After a destructive action, voice confirms before doing it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Loader2, Trash2, Mail, Fingerprint, ShieldCheck,
        AlertTriangle, Check, ArrowLeft, Languages, Clock } from "lucide-react";
import { useVoiceAction } from "@/context/VoiceActionsContext";
import "./AccountSettingsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

const MODULES = [
  { id: "fish-map",      label: "Fish Map" },
  { id: "tackle-shop",   label: "Tackle Shop" },
  { id: "fishing-tours", label: "Fishing Tours" },
  { id: "hustleup",      label: "HustleUp" },
  { id: "hustlechat",    label: "HustleChat" },
  { id: "plaza",         label: "Plaza" },
  { id: "property",      label: "Property" },
];

/** Curated IANA timezone list — the mix Yates/Bella/HustleSuite tenants
 *  actually operate across. Sorted roughly by the AU business day so
 *  the local-first mental model wins. */
const TIMEZONES = [
  "Australia/Sydney",
  "Australia/Brisbane",
  "Australia/Melbourne",
  "Australia/Adelaide",
  "Australia/Perth",
  "Australia/Hobart",
  "Australia/Darwin",
  "Pacific/Auckland",
  "Pacific/Fiji",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Athens",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "UTC",
];

export default function AccountSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe]         = useState(null);
  const [prefs, setPrefs]   = useState([]);
  const [langPrefs, setLangPrefs] = useState({ preferred_language: null, notify_language: false });
  const [loading, setLoading] = useState(true);
  const [confirmModule, setConfirmModule] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  /* ── Load identity + email prefs ──────────────────────────── */
  useEffect(() => {
    let dead = false;
    Promise.allSettled([
      api.get(`/api/identity/me`),
      api.get(`/api/account/email-prefs`),
      api.get(`/api/auth/me/language-prefs`),
    ]).then(([m, p, lp]) => {
      if (dead) return;
      if (m.status === "fulfilled") setMe(m.value.data);
      if (p.status === "fulfilled") setPrefs(p.value.data.prefs || []);
      if (lp.status === "fulfilled") setLangPrefs(lp.value.data);
      setLoading(false);
    });
    return () => { dead = true; };
  }, []);

  /* ── Toggle one email preference ──────────────────────────── */
  const togglePref = async (module, list_id, subscribed) => {
    setBusy(true);
    await api.post(`/api/account/email-prefs/toggle`, {
      module, list_id, subscribed,
    });
    setPrefs((rows) => rows.map((r) =>
      (r.module === module && r.list_id === list_id)
        ? { ...r, subscribed } : r));
    setFlash(subscribed ? "Subscribed." : "Unsubscribed.");
    setTimeout(() => setFlash(""), 2400);
    setBusy(false);
  };

  /* ── Toggle notify_language (auto-translate emails/pushes) ─── */
  const toggleNotifyLanguage = async (enabled) => {
    setBusy(true);
    try {
      await api.patch(`/api/auth/me/notify-language`, { notify_language: enabled });
      setLangPrefs((p) => ({ ...p, notify_language: enabled }));
      setFlash(enabled
        ? `Emails & push will be translated to ${langPrefs.preferred_language || "your preferred language"}.`
        : "Emails & push will stay in English.");
    } catch (err) {
      setFlash(err?.response?.data?.detail || "Couldn't save — try again.");
    }
    setBusy(false);
    setTimeout(() => setFlash(""), 3200);
  };

  /* ── Save timezone — stamps every future HustleQR bulk-import batch
       in the user's local wall-clock time. */
  const saveTimezone = async (tz) => {
    setBusy(true);
    try {
      await api.patch(`/api/auth/me/timezone`, { timezone: tz });
      setLangPrefs((p) => ({ ...p, timezone: tz }));
      setFlash(`Timezone saved — future imports stamp in ${tz}.`);
    } catch (err) {
      setFlash(err?.response?.data?.detail || "Couldn't save — try again.");
    }
    setBusy(false);
    setTimeout(() => setFlash(""), 3200);
  };

  /* ── Open per-module deletion confirm ─────────────────────── */
  const openDelete = (modId) => setConfirmModule(modId);

  const performDelete = async () => {
    if (confirmText.toLowerCase() !== "delete") return;
    setBusy(true);
    try {
      const r = await api.post(`/api/account/delete-module`, {
        module: confirmModule, confirm_text: "delete",
      });
      setFlash(`Your ${confirmModule} account is gone.`);
      setConfirmModule(null);
      setConfirmText("");
      // Refresh identity to update modules_active
      const m = await api.get(`/api/identity/me`);
      setMe(m.data);
    } catch (err) {
      setFlash(err?.response?.data?.detail || "Couldn't delete — try again.");
    }
    setBusy(false);
    setTimeout(() => setFlash(""), 4000);
  };

  /* ── Biometric (passkey) enrolment ────────────────────────── */
  const enrolBiometric = async () => {
    if (!window.PublicKeyCredential) {
      setFlash("Your device doesn't support passkeys.");
      return;
    }
    setBusy(true);
    try {
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      const user_id = me?.hustle_id || me?.email || "anonymous";
      const cred = await navigator.credentials.create({
        publicKey: {
          challenge, rp: { name: "Hustle", id: window.location.hostname },
          user: {
            id:    new TextEncoder().encode(user_id),
            name:  me?.email || "user",
            displayName: me?.first_name || me?.email || "user",
          },
          pubKeyCredParams: [{ alg: -7, type: "public-key" }],
          authenticatorSelection: {
            residentKey: "preferred",
            userVerification: "required",
            authenticatorAttachment: "platform",
          },
          timeout: 60000, attestation: "none",
        },
      });
      const credId = btoa(String.fromCharCode.apply(null,
        new Uint8Array(cred.rawId)));
      await api.post(`/api/identity/passkey/register`, {
        credential_id: credId,
        public_key: "platform",
        device_name: navigator.userAgent.split(" ")[0] || "iPhone",
      });
      setFlash("Face ID / Touch ID enrolled.");
      setMe((m) => ({ ...m, biometric_enrolled: true }));
    } catch (err) {
      setFlash(err?.message || "Enrolment cancelled.");
    }
    setBusy(false);
    setTimeout(() => setFlash(""), 4000);
  };

  /* ── Group email prefs by module for the UI ───────────────── */
  const prefsByModule = useMemo(() => {
    const m = new Map();
    for (const p of prefs) {
      if (!m.has(p.module)) m.set(p.module, []);
      m.get(p.module).push(p);
    }
    return m;
  }, [prefs]);

  /* ── VOICE ACTIONS — every button is voice-callable ────────── */
  // Per-module delete (one registration per module)
  for (const mod of MODULES) {
    /* eslint-disable react-hooks/rules-of-hooks */
    useVoiceAction({
      phrases: [
        `delete my ${mod.label.toLowerCase()} account`,
        `remove my ${mod.label.toLowerCase()} account`,
        `cancel my ${mod.label.toLowerCase()}`,
      ],
      label: `Delete ${mod.label} account`,
      handler: () => openDelete(mod.id),
    });
    /* eslint-enable react-hooks/rules-of-hooks */
  }
  useVoiceAction({
    phrases: ["enable face id", "set up face id", "enable touch id",
               "enrol biometric", "enroll biometric", "turn on biometric"],
    label: "Enrol biometric login",
    handler: () => enrolBiometric(),
  });
  useVoiceAction({
    phrases: ["confirm delete", "yes delete it", "yes confirm",
               "go ahead and delete", "do it"],
    label: "Confirm deletion",
    handler: () => {
      if (confirmModule) { setConfirmText("delete"); setTimeout(performDelete, 200); }
    },
  });
  useVoiceAction({
    phrases: ["cancel", "stop", "never mind", "abort"],
    label: "Cancel",
    handler: () => { setConfirmModule(null); setConfirmText(""); },
  });

  useVoiceAction({
    phrases: ["translate my emails", "auto translate notifications",
               "translate notifications", "email in my language",
               "notifications in my language"],
    label: "Turn on translated notifications",
    handler: () => toggleNotifyLanguage(true),
  });
  useVoiceAction({
    phrases: ["stop translating emails", "notifications in english",
               "turn off translated notifications", "english emails only"],
    label: "Turn off translated notifications",
    handler: () => toggleNotifyLanguage(false),
  });

  if (loading) return (
    <div className="acct-root">
      <div className="acct-loading"><Loader2 className="acct-spin"/> Loading…</div>
    </div>
  );

  return (
    <div className="acct-root" data-testid="account-settings-root">
      <header className="acct-top">
        <button onClick={() => navigate(-1)} className="acct-back"
                data-testid="acct-back">
          <ArrowLeft size={14}/> Back
        </button>
        <h1>Account &amp; privacy</h1>
      </header>

      {flash && <div className="acct-flash" data-testid="acct-flash">{flash}</div>}

      {/* Identity */}
      <section className="acct-section">
        <h2><ShieldCheck size={16}/> Your Hustle identity</h2>
        <div className="acct-id">
          <div><span>Email</span><strong>{me?.email || "—"}</strong></div>
          <div><span>Display name</span><strong>{me?.display_name || me?.first_name || "—"}</strong></div>
          <div><span>Hustle ID</span><strong>{me?.hustle_id || "—"}</strong></div>
        </div>
      </section>

      {/* Biometric */}
      <section className="acct-section">
        <h2><Fingerprint size={16}/> Face ID / Touch ID / Fingerprint</h2>
        <p className="acct-help">Sign in without typing your password. Your device handles the biometric — Hustle only stores a passkey credential ID.</p>
        {me?.biometric_enrolled ? (
          <div className="acct-pill acct-pill--ok"><Check size={13}/> Enrolled</div>
        ) : (
          <button className="acct-btn acct-btn--primary"
                   onClick={enrolBiometric}
                   disabled={busy}
                   data-testid="acct-biometric-enrol">
            Enable biometric login
          </button>
        )}
      </section>

      {/* Language & notifications */}
      <section className="acct-section" data-testid="acct-language-section">
        <h2><Languages size={16}/> Language &amp; notifications</h2>
        <p className="acct-help">
          Your app language is set from the language bar at the top of the
          page. Emails, push notifications and receipts stay in English by
          default — flip the switch below if you&apos;d like them translated
          into your preferred language.
        </p>
        <div className="acct-id" data-testid="acct-lang-summary">
          <div>
            <span>App language</span>
            <strong>{langPrefs.preferred_language || "English (default)"}</strong>
          </div>
        </div>
        <label className="acct-pref-row" data-testid="acct-pref-notify-language">
          <input
            type="checkbox"
            checked={!!langPrefs.notify_language}
            onChange={(e) => toggleNotifyLanguage(e.target.checked)}
            disabled={busy || !langPrefs.preferred_language}
            data-testid="acct-notify-language-toggle"
          />
          <span>
            Translate my emails, receipts &amp; push notifications into
            {" "}<strong>{langPrefs.preferred_language || "my preferred language"}</strong>
            {!langPrefs.preferred_language && (
              <em style={{display:"block", opacity:.75, fontSize:12, marginTop:4}}>
                Pick a preferred language first — tap the language bar at
                the top of the page.
              </em>
            )}
          </span>
        </label>
      </section>

      {/* Timezone — used to stamp HustleQR bulk-import batches in the
           member's local wall-clock time. */}
      <section className="acct-section" data-testid="acct-timezone-section">
        <h2><Clock size={16}/> Timezone</h2>
        <p className="acct-help">
          Sets the local wall-clock time we stamp on your HustleQR bulk-import
          batches, receipts and scheduled reports. Defaults to Australia/Sydney
          if unset. Pick your closest city — the list follows the IANA standard.
        </p>
        <div className="acct-id" data-testid="acct-tz-summary">
          <div>
            <span>Current timezone</span>
            <strong>{langPrefs.timezone || "Australia/Sydney (default)"}</strong>
          </div>
        </div>
        <label className="acct-pref-row" data-testid="acct-pref-timezone">
          <span>Change timezone</span>
          <select
            value={langPrefs.timezone || "Australia/Sydney"}
            disabled={busy}
            onChange={(e) => saveTimezone(e.target.value)}
            data-testid="acct-tz-select"
            style={{ padding: "6px 10px", border: "1px solid #d1d5db",
                     borderRadius: 6, minWidth: 240, fontSize: 13 }}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </label>
      </section>

      {/* Email preferences */}
      <section className="acct-section">
        <h2><Mail size={16}/> Email preferences</h2>
        <p className="acct-help">Per-module, per-list. Unsubscribing from one list <em>never</em> affects another.</p>
        {[...prefsByModule.entries()].map(([modId, lists]) => (
          <div key={modId} className="acct-prefs-mod">
            <h3>{MODULES.find((m) => m.id === modId)?.label || modId}</h3>
            {lists.map((l) => (
              <label key={l.list_id} className="acct-pref-row"
                     data-testid={`acct-pref-${modId}-${l.list_id}`}>
                <input
                  type="checkbox"
                  checked={l.subscribed}
                  onChange={(e) => togglePref(modId, l.list_id, e.target.checked)}
                  disabled={busy}
                />
                <span>{l.label}</span>
              </label>
            ))}
          </div>
        ))}
      </section>

      {/* Per-module deletion */}
      <section className="acct-section acct-section--danger">
        <h2><Trash2 size={16}/> Delete an account</h2>
        <p className="acct-help">
          Removes your data in ONE module only. Your other Hustle modules
          keep working. Some legally-retained rows (tax-relevant orders,
          trust-account ledger entries) are anonymised rather than deleted.
        </p>
        <div className="acct-mods">
          {MODULES.map((m) => {
            const active = me?.modules_active?.includes(m.id);
            const gone   = me?.modules_deleted?.includes(m.id);
            return (
              <div className="acct-mod" key={m.id}>
                <div>
                  <strong>{m.label}</strong>
                  {gone && <span className="acct-mod__status">deleted</span>}
                  {active && !gone && <span className="acct-mod__status acct-mod__status--ok">active</span>}
                </div>
                <button
                  className="acct-btn acct-btn--ghost"
                  onClick={() => openDelete(m.id)}
                  disabled={gone || busy}
                  data-testid={`acct-delete-${m.id}`}
                >
                  {gone ? "Already deleted" : "Delete"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Hint at voice */}
      <p className="acct-voice-hint">
        Tip: tap the 🎤 and say <em>"delete my Tackle Shop account"</em> or
        <em> "enable Face ID"</em>.
      </p>

      <p className="acct-fineprint">
        Need everything gone in one go? Email <a href="mailto:privacy@hustleplaza.au">privacy@hustleplaza.au</a> to request full master-identity deletion.
      </p>

      {/* Confirm dialog */}
      {confirmModule && (
        <div className="acct-modal" data-testid="acct-confirm-modal">
          <div className="acct-modal__card">
            <AlertTriangle size={28} className="acct-modal__icon"/>
            <h3>Delete your {MODULES.find((m) => m.id === confirmModule)?.label} account?</h3>
            <p>This is permanent. Type <strong>delete</strong> below or say <em>"confirm delete"</em>.</p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='type "delete"'
              autoFocus
              data-testid="acct-confirm-input"
            />
            <div className="acct-modal__actions">
              <button onClick={() => { setConfirmModule(null); setConfirmText(""); }}
                       data-testid="acct-confirm-cancel">Cancel</button>
              <button
                className="acct-btn--danger"
                disabled={confirmText.toLowerCase() !== "delete" || busy}
                onClick={performDelete}
                data-testid="acct-confirm-delete"
              >
                {busy ? <Loader2 className="acct-spin" size={14}/> : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
